import { stripEntityReferences } from '@apps/web/lib/entity_references';
import { type TrainingRunRead } from '@libraries/typescript/api_client';

export type TrainingRunGroup = {
    // The scheduler's user id, or 'phoebe' for the no-scheduler group.
    readonly key: string;
    readonly label: string;
    // Phoebe acted on its own here: voice calls and texts have no scheduler.
    readonly isPhoebe: boolean;
    // The current viewer's own group (pinned to the top).
    readonly isOwn: boolean;
    readonly runs: ReadonlyArray<TrainingRunRead>;
};

const PHOEBE_GROUP_KEY = 'phoebe';

/**
 * Group training items by the scheduler who ran them, with everything Phoebe
 * did unprompted (voice calls, texts) under one "Phoebe" group. What each item
 * IS — chat, outreach, automation, voice, text — is a label on the item itself
 * (``runTypeLabel``) rather than a second layer of groups, so the view stays
 * two levels deep.
 *
 * An automation groups under the person who set it up, and its row says
 * "Automation", so a run that fired overnight is not mistaken for one they ran.
 *
 * Runs stay in their given (newest-first) order within a group. Group order
 * depends on `orderBy`:
 * - 'unreviewed' (default, for the Unreviewed tab): the viewer's own group
 *   pinned to the top, then the rest ordered most-unreviewed-first (ties
 *   broken alphabetically), with the Phoebe group always last.
 * - 'recency' (for Reviewed/All, which mix reviewed and unreviewed items so a
 *   backlog count would not mean much): groups ordered by their most recent
 *   run, so the feed still reads newest-to-oldest.
 */
export function groupRunsByScheduler(
    runs: ReadonlyArray<TrainingRunRead>,
    currentUserId?: string | null,
    options?: { readonly orderBy?: 'unreviewed' | 'recency' },
): Array<TrainingRunGroup> {
    const groupsByKey = new Map<
        string,
        {
            key: string;
            label: string;
            isPhoebe: boolean;
            isOwn: boolean;
            runs: Array<TrainingRunRead>;
            // The first run added to a group is its most recent, since `runs`
            // arrives newest-first — kept alongside so 'recency' ordering
            // never needs to index into `runs`.
            mostRecentAt: string;
        }
    >();
    for (const run of runs) {
        const schedulerId = run.created_by_user_id;
        const isPhoebe = schedulerId === null;
        const key = isPhoebe ? PHOEBE_GROUP_KEY : schedulerId;
        const label = isPhoebe
            ? 'Phoebe'
            : (run.created_by_display_name ?? 'Unknown');
        const existing = groupsByKey.get(key);
        if (existing) {
            existing.runs.push(run);
        } else {
            groupsByKey.set(key, {
                key,
                label,
                isPhoebe,
                isOwn: !isPhoebe && key === currentUserId,
                runs: [run],
                mostRecentAt: run.last_activity_at,
            });
        }
    }
    const groups = [...groupsByKey.values()];
    if (options?.orderBy === 'recency') {
        return groups.sort(
            (a, b) => Date.parse(b.mostRecentAt) - Date.parse(a.mostRecentAt),
        );
    }
    return groups.sort((a, b) => {
        // Everything Phoebe did on its own trails the schedulers.
        if (a.isPhoebe !== b.isPhoebe) {
            return a.isPhoebe ? 1 : -1;
        }
        // The viewer's own runs lead, ahead of everyone else's. With no
        // currentUserId no group is "own", so this is a no-op then.
        if (a.isOwn !== b.isOwn) {
            return a.isOwn ? -1 : 1;
        }
        if (b.runs.length !== a.runs.length) {
            return b.runs.length - a.runs.length;
        }
        return a.label.localeCompare(b.label);
    });
}

export function runTypeLabel(run: TrainingRunRead): string {
    if (run.channel === 'voice') {
        return 'Voice';
    }
    if (run.channel === 'sms') {
        return 'Text';
    }
    switch (run.run_type) {
        case 'general':
            return 'Chat';
        case 'outreach_analysis':
            return 'Outreach';
        case 'automation':
            return 'Automation';
        default:
            return 'Chat';
    }
}

/**
 * Who ran it. Voice calls and texts have no scheduler — Phoebe made those on
 * its own. What the item IS rides alongside as ``runTypeLabel``, so an
 * automation shows both the person who set it up and that it fired on its own.
 */
export function homeRunLabel(run: TrainingRunRead): string {
    return run.created_by_display_name ?? 'Phoebe';
}

export function runTitle(run: TrainingRunRead): string {
    const title = stripEntityReferences(run.title ?? '').trim();
    if (title) {
        return title;
    }
    // Voice rows have no title until a call summary exists, and SMS rows
    // fall back only when the counterpart contact is missing — use a
    // channel label instead of the chat default.
    if (run.channel === 'voice') {
        return 'Voice call';
    }
    if (run.channel === 'sms') {
        return 'Text conversation';
    }
    return 'New AI chat';
}
